import type { Comment, CreateCommentInput } from "@adafia/types";
import { useAuth } from "@adafia/ui";
import { Trash2 } from "lucide-react";
import { CommentForm } from "./CommentForm";

interface CommentSectionProps {
	comments?: Comment[];
	onCommentSubmit: (data: Omit<CreateCommentInput, "nodeId">) => void;
	onCommentDelete?: (commentId: string) => void;
	isCommentPending: boolean;
}

export const CommentSection = ({
	comments,
	onCommentSubmit,
	onCommentDelete,
	isCommentPending,
}: CommentSectionProps) => {
	const { user } = useAuth();
	const isAdmin = user?.role === "admin";
	const generalComments = comments?.filter((c) => !c.blockId) || [];

	return (
		<section id="comments" className="comments-section">
			<h3>General Comments</h3>

			<div className="comments-list">
				{generalComments.map((comment) => (
					<div key={comment.id} className="comment-item">
						<div
							className="comment-header"
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "baseline",
									gap: "0.5rem",
								}}
							>
								<span className="comment-author">{comment.authorName}</span>
								<span className="comment-date">
									{comment.createdAt
										? new Date(comment.createdAt).toLocaleDateString()
										: "Just now"}
								</span>
							</div>
							{isAdmin && comment.id && onCommentDelete && (
								<button
									type="button"
									onClick={() => {
										const commentId = comment.id;
										if (commentId) {
											onCommentDelete(commentId);
										}
									}}
									className="comment-delete-btn"
									title="Delete comment"
									style={{
										background: "none",
										border: "none",
										color: "var(--color-error, #ef4444)",
										cursor: "pointer",
										padding: "4px",
										display: "flex",
										alignItems: "center",
										opacity: 0.6,
										transition: "opacity 0.2s",
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.opacity = "1";
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.opacity = "0.6";
									}}
								>
									<Trash2 size={14} />
								</button>
							)}
						</div>
						<p className="comment-content">{comment.content}</p>
					</div>
				))}
				{generalComments.length === 0 && (
					<p style={{ color: "var(--color-text-muted)", margin: "1rem 0" }}>
						No general comments yet. Be the first to share your thoughts!
					</p>
				)}
			</div>

			<CommentForm
				title="Leave a general comment"
				isLoading={isCommentPending}
				onSubmit={(data) => onCommentSubmit({ ...data, blockId: null })}
			/>
		</section>
	);
};
